import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
//components
import { TextField, PasswordField } from '../../components/hook-form';
// @form
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// cookie
import { useCookies } from 'react-cookie';
// utils
import userAgent from '../../utils/user-agent';
import messages from '../../utils/messages';
import { REGEXP_ID, REGEXP_ID_START, REGEXP_PASSWORD } from '../../utils/regexp';
// styles
import { LoginWrapper, LoginVisual, LoginForm, PrimaryButton, Message } from '../../styles';
// apis
import { login } from '../../apis';

// ----------------------------------------------------------------------

const schema = yup.object().shape({
  id: yup.string()
    .required()
    .matches(REGEXP_ID)
    .matches(REGEXP_ID_START),
  password: yup.string()
    .required()
    .matches(REGEXP_PASSWORD)
});

interface FormValues {
  id?: string;
  password?: string;
};

// ----------------------------------------------------------------------

export default function LoginPage() {
  const navigate  = useNavigate();
  
  const {
    control,
    setFocus,
    setValue,
    handleSubmit
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      id: '',
      password: ''
    }
  });

  const [ message, setMessage ] = useState(null);

  const [ checked, setChecked ] = useState(false);
  const [ cookies, setCookie, removeCookie ] = useCookies(['userid']);

  useEffect(() => {
    if ( cookies.userid !== undefined ) {
      setValue('id', cookies.userid);
      setChecked(true);
    }
  }, []);

  const onError = (error: object) => {
    setMessage( messages('default', 'LOGIN') );
    if ( Object.keys(error).includes('id') ) {
      setValue('id', '');
      setFocus('id');
    } else {
      setFocus('password');
    }
    setValue('password', '');
  };

  const onSubmit = async (data: FormValues) => {
      if ( message ) setMessage(null);

      const UA = userAgent();
      await login({ identification: data.id, password: data.password, ...UA })
        .then((response) => {
          if ( checked ) {
            setCookie('userid', data.id, { maxAge: 7776000 });
          } else {
            removeCookie('userid');
          }
          const { temp, expiration } = response;
          if ( temp || expiration ) return navigate('/change-password', {state: { temp, expiration }});
          return navigate('/real-time', {state: { submit: true }});
        })
        .catch((error) => {
          const code = error.response.data.code;
          if ( code === 'ERR-023' ) return navigate('/restrict');
          
          setMessage( messages(code, 'LOGIN') );
          setValue('password', '');
          setFocus('password');
        });
  };

  return (
    <LoginWrapper>
      <LoginVisual />

      <LoginForm>
        <form onSubmit={ handleSubmit(onSubmit, onError) }>
          <div />

          <TextField name='id' control={control} label='아이디' type='text' maxLength={30} autoComplete='username' />

          <PasswordField
            name='password'
            control={control}
            label='비밀번호'
            maxLength={30}
            autoComplete='current-password'
          />

          <Message $visible={message ? true : false}>{ message }</Message>

          <PrimaryButton type='submit'>로그인</PrimaryButton>

          <div>
            <label>
              <input type='checkbox' id='rememberId' checked={checked} onChange={(event) => setChecked(event.target.checked)} />
              <label htmlFor='rememberId' />
              아이디 저장
            </label>

            <p>비밀번호를 잊으셨나요? <Link to='/find-password'>비밀번호 찾기</Link></p>
          </div>
        </form>
      </LoginForm>
    </LoginWrapper>
  );
}